import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
  ensureAlbumAccess,
  getAlbumByIdRecord,
  getInviteByToken,
  markInviteAsUsed,
} from '../config/inviteAccess';

const normalize = (value) => String(value || '').trim().toLowerCase();

function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const {
    user,
    hasConfig,
    loading,
    signOutUser,
    signUpWithEmail,
  } = useAuth();

  const [inviteState, setInviteState] = useState({
    loading: true,
    status: 'checking',
    invite: null,
    inviteDocId: null,
    albumSlug: '',
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const inviteResult = await getInviteByToken(token);
      if (!alive) return;

      if (inviteResult.status !== 'valid') {
        setInviteState({
          loading: false,
          status: inviteResult.status,
          invite: inviteResult.invite,
          inviteDocId: inviteResult.inviteDocId,
          albumSlug: '',
        });
        return;
      }

      const albumDoc = inviteResult.invite?.albumId
        ? await getAlbumByIdRecord(inviteResult.invite.albumId)
        : null;
      const albumSlug = inviteResult.invite?.albumSlug || albumDoc?.slug || '';

      setInviteState({
        loading: false,
        status: albumSlug ? 'valid' : 'invalid',
        invite: inviteResult.invite,
        inviteDocId: inviteResult.inviteDocId,
        albumSlug,
      });
    };

    run();
    return () => {
      alive = false;
    };
  }, [token]);

  const inviteEmail = useMemo(
    () => normalize(inviteState.invite?.email),
    [inviteState.invite],
  );

  useEffect(() => {
    let alive = true;

    const redeemLoggedInUser = async () => {
      if (!user || inviteState.status !== 'valid' || !inviteState.invite) return;
      if (normalize(user.email) !== inviteEmail) return;

      try {
        await ensureAlbumAccess({
          albumId: inviteState.invite.albumId,
          userUid: user.uid,
          role: inviteState.invite.role || 'viewer',
        });
        await markInviteAsUsed(inviteState.inviteDocId);
        sessionStorage.setItem('selectedProfile', inviteState.albumSlug);
        if (alive) navigate(`/v/${inviteState.albumSlug}`, { replace: true });
      } catch (err) {
        if (alive) {
          setError(err?.message || 'Could not complete invite redemption.');
        }
      }
    };

    redeemLoggedInUser();
    return () => {
      alive = false;
    };
  }, [
    inviteEmail,
    inviteState.albumSlug,
    inviteState.invite,
    inviteState.inviteDocId,
    inviteState.status,
    navigate,
    user,
  ]);

  if (loading || inviteState.loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#141414', color: '#fff', display: 'grid', placeItems: 'center', padding: '1.5rem' }}>
        <p>Checking your invite...</p>
      </main>
    );
  }

  if (!hasConfig) {
    return (
      <main style={{ minHeight: '100vh', background: '#141414', color: '#fff', display: 'grid', placeItems: 'center', padding: '1.5rem' }}>
        <section style={{ maxWidth: 520, textAlign: 'center' }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Invite Access Unavailable</h1>
          <p style={{ color: '#d1d5db' }}>Firebase configuration is missing. Please set your environment variables.</p>
          <p style={{ marginTop: '1rem' }}><Link to="/login" style={{ color: '#fff' }}>Back to Login</Link></p>
        </section>
      </main>
    );
  }

  if (inviteState.status === 'invalid') {
    return <Navigate to="/login" replace />;
  }

  if (inviteState.status === 'expired') {
    return (
      <main style={{ minHeight: '100vh', background: '#141414', color: '#fff', display: 'grid', placeItems: 'center', padding: '1.5rem' }}>
        <section style={{ maxWidth: 520, textAlign: 'center' }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Invitation Expired</h1>
          <p style={{ color: '#d1d5db' }}>This invite has expired. Ask the sender for a fresh link.</p>
          <p style={{ marginTop: '1rem' }}><Link to="/login" style={{ color: '#fff' }}>Go to Login</Link></p>
        </section>
      </main>
    );
  }

  if (inviteState.status === 'used') {
    return (
      <main style={{ minHeight: '100vh', background: '#141414', color: '#fff', display: 'grid', placeItems: 'center', padding: '1.5rem' }}>
        <section style={{ maxWidth: 520, textAlign: 'center' }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Invite Already Used</h1>
          <p style={{ color: '#d1d5db' }}>This invite was already redeemed. Sign in with the linked account to continue.</p>
          <p style={{ marginTop: '1rem' }}><Link to="/login" style={{ color: '#fff' }}>Go to Login</Link></p>
        </section>
      </main>
    );
  }

  if (user && normalize(user.email) !== inviteEmail) {
    return (
      <main style={{ minHeight: '100vh', background: '#141414', color: '#fff', display: 'grid', placeItems: 'center', padding: '1.5rem' }}>
        <section style={{ maxWidth: 560, textAlign: 'center' }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Wrong Account</h1>
          <p style={{ color: '#d1d5db' }}>
            This invite is linked to
            {' '}
            <strong>{inviteState.invite.email}</strong>
            .
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={signOutUser}
              style={{ padding: '0.7rem 1rem', borderRadius: 6, border: 0, background: '#e50914', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
            >
              Sign out and switch account
            </button>
            <Link
              to="/login"
              style={{ padding: '0.7rem 1rem', borderRadius: 6, border: '1px solid rgba(255,255,255,0.35)', color: '#fff', textDecoration: 'none' }}
            >
              Go to Login
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    if (!password || password.length < 6) {
      setSubmitting(false);
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitting(false);
      setError('Passwords do not match.');
      return;
    }

    try {
      const created = await signUpWithEmail(inviteState.invite.email, password);
      await ensureAlbumAccess({
        albumId: inviteState.invite.albumId,
        userUid: created.user.uid,
        role: inviteState.invite.role || 'viewer',
      });
      await markInviteAsUsed(inviteState.inviteDocId);
      sessionStorage.setItem('selectedProfile', inviteState.albumSlug);
      navigate(`/v/${inviteState.albumSlug}`, { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not create account from invite.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{ minHeight: '100vh', background: '#141414', color: '#fff', padding: '2rem 1rem' }}>
      <section style={{ maxWidth: 460, margin: '2rem auto', background: 'rgba(0,0,0,0.72)', padding: '2rem', borderRadius: 8 }}>
        <h1 style={{ marginBottom: '0.8rem', fontSize: '2rem' }}>Create your account</h1>
        <p style={{ color: '#d1d5db', marginBottom: '1rem' }}>
          You&apos;re invited to this Veeflix premiere. Create your login to continue.
        </p>

        <form onSubmit={handleCreateAccount}>
          <input
            type="email"
            value={inviteState.invite.email}
            disabled
            style={{
              width: '100%',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
            }}
          />
          <input
            type="password"
            placeholder="Create password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            style={{ width: '100%', marginBottom: '0.75rem', padding: '0.75rem', borderRadius: 6, border: 0 }}
            minLength={6}
            required
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            style={{ width: '100%', marginBottom: '0.75rem', padding: '0.75rem', borderRadius: 6, border: 0 }}
            minLength={6}
            required
          />
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: 6,
              border: 0,
              background: '#e50914',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            Create Account
          </button>
        </form>

        {error ? <p style={{ color: '#fca5a5', marginTop: '0.75rem' }}>{error}</p> : null}
      </section>
    </main>
  );
}

export default InvitePage;
